/* Log dock — collapses to a one-line ticker. Chat lives in the same stream as events
 * so it appears in replay and AAR (FR-COLLAB-01).
 */
import { ChevronDown, ChevronUp, MessageSquare } from 'lucide-react'
import { useState } from 'react'
import { useDeck } from '../state/store'
import type { LogEntry } from '../data/types'

type Filter = 'all' | 'warn' | 'chat'

function severityToken(s: LogEntry['severity']): string {
  switch (s) {
    case 'crit':
      return 'var(--color-crit)'
    case 'warn':
      return 'var(--color-warn)'
    case 'chat':
      return 'var(--color-accent-hi)'
    default:
      return 'var(--color-text-3)'
  }
}

function severityLabel(s: LogEntry['severity']): string {
  return s === 'crit' ? 'CRIT' : s === 'warn' ? 'WARN' : s === 'chat' ? 'CHAT' : 'INFO'
}

function Entry({ e }: { e: LogEntry }) {
  return (
    <div
      className="flex items-baseline gap-2 px-3 py-1 transition-colors hover:bg-[var(--color-surface-2)]"
      style={{ borderBottom: '1px solid var(--color-line-1)' }}
    >
      <span className="num shrink-0" style={{ fontSize: 10, color: 'var(--color-text-3)' }}>
        {e.simTime}
      </span>
      <span
        className="label shrink-0"
        style={{ fontSize: 8.5, minWidth: 30, color: severityToken(e.severity) }}
      >
        {severityLabel(e.severity)}
      </span>
      <span
        className="num shrink-0 truncate"
        style={{ fontSize: 10.5, maxWidth: 130, color: 'var(--color-text-2)' }}
      >
        {e.source}
      </span>
      <span className="min-w-0 flex-1" style={{ fontSize: 11.5, color: 'var(--color-text-1)' }}>
        {e.message}
      </span>
    </div>
  )
}

export function LogDock() {
  const open = useDeck((s) => s.logOpen)
  const toggle = useDeck((s) => s.toggleLog)
  const log = useDeck((s) => s.log)
  const [filter, setFilter] = useState<Filter>('all')

  const shown = log.filter((e) => {
    if (filter === 'all') return true
    if (filter === 'warn') return e.severity === 'warn' || e.severity === 'crit'
    return e.severity === 'chat'
  })

  const latest = log[0]

  return (
    <section
      className="shrink-0 border-t"
      style={{
        background: 'var(--color-surface-1)',
        borderColor: 'var(--color-line-2)',
        height: open ? 172 : 28,
        transition: 'height 180ms var(--ease-out)',
      }}
      aria-label="Message log"
    >
      {/* Header / ticker */}
      <div
        className="flex h-7 items-center gap-2 px-3"
        style={{ borderBottom: open ? '1px solid var(--color-line-2)' : undefined }}
      >
        <button
          className="ctl"
          style={{ minHeight: 20, padding: '0 4px' }}
          onClick={toggle}
          aria-expanded={open}
          aria-label={open ? 'Collapse message log' : 'Expand message log'}
          title={open ? 'Collapse  (`)' : 'Expand  (`)'}
        >
          {open ? <ChevronDown size={13} /> : <ChevronUp size={13} />}
          <span className="label" style={{ fontSize: 9 }}>
            Log
          </span>
        </button>

        {!open && latest && (
          <div className="flex min-w-0 flex-1 items-baseline gap-2">
            <span className="num shrink-0" style={{ fontSize: 10, color: 'var(--color-text-3)' }}>
              {latest.simTime}
            </span>
            <span className="truncate" style={{ fontSize: 11, color: 'var(--color-text-2)' }}>
              {latest.message}
            </span>
          </div>
        )}

        {open && (
          <>
            <div
              className="flex items-center gap-px rounded p-px"
              style={{ background: 'var(--color-surface-0)' }}
              role="group"
              aria-label="Filter log"
            >
              {(['all', 'warn', 'chat'] as Filter[]).map((f) => (
                <button
                  key={f}
                  className="ctl"
                  style={{ minHeight: 18, fontSize: 10, padding: '0 6px' }}
                  data-active={filter === f}
                  onClick={() => setFilter(f)}
                  aria-pressed={filter === f}
                >
                  {f === 'all' ? 'All' : f === 'warn' ? 'Alerts' : 'Chat'}
                </button>
              ))}
            </div>
            <span className="num ml-auto" style={{ fontSize: 10, color: 'var(--color-text-3)' }}>
              {shown.length} / {log.length}
            </span>
          </>
        )}
      </div>

      {open && (
        <>
          <div className="overflow-y-auto" style={{ height: 108 }}>
            {shown.map((e) => (
              <Entry key={e.id} e={e} />
            ))}
          </div>

          {/* Chat input — recorded into the event stream, so it replays */}
          <div className="flex h-9 items-center gap-2 px-3" style={{ borderTop: '1px solid var(--color-line-2)' }}>
            <MessageSquare size={13} style={{ color: 'var(--color-text-3)' }} aria-hidden />
            <label htmlFor="chat" className="label" style={{ fontSize: 9 }}>
              BLUE
            </label>
            <input
              id="chat"
              type="text"
              placeholder="Message side channel…"
              className="min-w-0 flex-1 rounded px-2 py-1 outline-none"
              style={{
                background: 'var(--color-surface-0)',
                border: '1px solid var(--color-line-2)',
                fontSize: 11.5,
                color: 'var(--color-text-1)',
              }}
            />
          </div>
        </>
      )}
    </section>
  )
}
